import React, { useCallback, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  popEmeraldModal,
  pushEmeraldModal,
} from 'src/common/emerald-modal-manager/emerald-modal-manager';
import { ICustomer } from 'src/common/interfaces/dtos/ICustomer';
import { ICustomerSearchCriteria } from 'src/common/interfaces/dtos/ICustomerSearchCriteria';
import { IPagedData } from 'src/common/interfaces/dtos/IPagedData';
import { AppState } from 'src/common/redux/configure-store';
import { CustomerLoyaltySearchForm } from './customer-loyalty-search-form';
import SearchCustomerResults from './customer-loyalty-search-results';

export const closeCustomerLoyaltySearch = (): void => {
  popEmeraldModal(LOYALTY_MODAL);
};
const LOYALTY_MODAL = 'LOYALTY_MODAL';
export const showCustomerLoyaltySearch = (props: ICustomerLoyaltyProps): void => {
  pushEmeraldModal(<CustomerLoyaltySearch {...props} />, undefined, LOYALTY_MODAL);
};

export const CustomerLoyaltySearch = ({
  onSearch,
  onSubmit,
  onClose,
}: ICustomerLoyaltyProps): JSX.Element => {
  const trainingModeActive = useSelector((state: AppState): boolean => state.posMode.trainingMode);

  const currentPage = useRef<number>(0);
  const initialCustomerData: IPagedData<ICustomer> = {
    lastPage: true,
    totalPages: 0,
    pageNumber: 0,
    totalResults: 0,
    pageContent: [],
  };
  const [searchCriteria, setSearchCriteria] = useState<Partial<ICustomerSearchCriteria>>({});
  const [customerDisplayData, setCustomerDisplayData] = useState<IPagedData<ICustomer>>(
    initialCustomerData,
  );

  const handleSearch = useCallback(
    (criteria: Partial<ICustomerSearchCriteria>): void => {
      setSearchCriteria({ ...criteria });
      onSearch(criteria, currentPage.current).then(pageData => {
        currentPage.current = pageData.pageNumber;
        setCustomerDisplayData(pageData);
      });
    },
    [onSearch],
  );

  const onBack = (): void => {
    currentPage.current = 0;
    setCustomerDisplayData(initialCustomerData);
  };

  const onNewPageHandler = (newPage: number): void => {
    if (newPage > currentPage.current) {
      currentPage.current++;
    } else {
      currentPage.current--;
    }
    handleSearch(searchCriteria);
  };

  return (
    <>
      {!customerDisplayData?.totalPages && (
        <CustomerLoyaltySearchForm
          onClose={onClose}
          trainingModeActive={trainingModeActive}
          onSearch={handleSearch}
        ></CustomerLoyaltySearchForm>
      )}
      {customerDisplayData?.totalPages && (
        <SearchCustomerResults
          onClose={onClose}
          onBack={onBack}
          onSubmit={onSubmit}
          updateCurrentPageHandler={onNewPageHandler}
          customerDisplayData={customerDisplayData}
          currentPage={Math.min(currentPage.current + 1, customerDisplayData.totalPages)}
          trainingModeActive={!!trainingModeActive}
        />
      )}
    </>
  );
};

export interface ICustomerLoyaltyProps {
  onSearch: (
    searchCriteria: Partial<ICustomerSearchCriteria>,
    pageNumber: number,
  ) => Promise<IPagedData<ICustomer>>;
  onSubmit: (customer: ICustomer) => void;
  onClose: () => void;
}
